import { useGlobalQuery } from '@/state/query-client/hooks';
import { requestTemplateVariables, type TemplateVariableRecord } from '@/graphql/queries/templates';
import type { TemplateVariable } from '../types/templates.types';

const strings = (values: Array<string | null> | null | undefined): string[] | undefined => {
  if (!values) return undefined;
  return values.filter((value): value is string => value != null);
};

const toTemplateVariable = (v: TemplateVariableRecord): TemplateVariable => ({
  id: v.ItemId,
  createdAt: v.CreatedDate ?? undefined,
  organizationIds: strings(v.OrganizationIds),
  templateId: v.TemplateId ?? '',
  parentId: v.ParentId ?? null,
  token: v.Token ?? undefined,
  name: v.Name ?? undefined,
  label: v.Label ?? undefined,
  type: v.Type ?? undefined,
  required: v.Required ?? undefined,
  placeholder: v.Placeholder ?? undefined,
  helpText: v.HelpText ?? undefined,
  options: strings(v.Options),
  defaultValue: v.DefaultValue ?? undefined,
  format: v.Format ?? undefined,
  typeMeta: v.TypeMeta ?? undefined,
  order: v.Order ?? undefined,
  sourceSpan: v.SourceSpan ?? undefined,
  uilmLabelKey: v.UilmLabelKey ?? undefined,
  uilmPlaceholderKey: v.UilmPlaceholderKey ?? undefined,
  uilmHelpTextKey: v.UilmHelpTextKey ?? undefined,
  uilmOptionsKeys: strings(v.UilmOptionsKeys),
});

export const fetchTemplateVariablesByTemplateId = async (templateId: string) => {
  try {
    const response = await requestTemplateVariables({
      where: { TemplateId: { eq: templateId } },
      paging: { pageNo: 1, pageSize: 200 },
    });
    const result = response?.getTemplateVariabless || { items: [], totalCount: 0 };
    return {
      ...result,
      items: (result.items || []).map(toTemplateVariable),
    };
  } catch (error) {
    console.error('Error fetching template variables:', error);
    return { items: [] as TemplateVariable[], totalCount: 0 };
  }
};

export const useGetTemplateVariablesByTemplateId = (templateId: string | undefined) => {
  return useGlobalQuery({
    queryKey: ['templateVariables', templateId],
    queryFn: () => fetchTemplateVariablesByTemplateId(templateId!),
    enabled: !!templateId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
